// Consultas simples ao Overpass API (OpenStreetMap).
// Cobre POIs (semáforos, paradas de ônibus) e velocidade máxima da via.

const ENDPOINTS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
];

// Incidente 2026-10-27 ("net::ERR_INSUFFICIENT_RESOURCES" em todas as
// chamadas pra api.gpsdrive.com.br, mapa cinza, "tentando reconectar"):
// useSpeedLimitAlerts dispara uma consulta por veículo em movimento a cada
// atualização da frota; o fetch não tinha tempo limite nem teto de
// simultâneas, então com o Overpass público lento/recusando (overpass-api.de
// dava ERR_CONNECTION_REFUSED) as consultas abandonadas se acumulavam sem fim
// (medido: 277 abertas em 2 min e subindo) até o Chrome recusar QUALQUER
// requisição nova da aba. Agora: 1 consulta por vez, 8 s de limite, e uma
// falha pausa o Overpass por 5 min (o alerta de velocidade só deixa de
// aparecer nesse tempo — nada mais depende disso).
const OVERPASS_TIMEOUT_MS = 8000;
const OVERPASS_PAUSE_AFTER_FAIL_MS = 5 * 60_000;
let overpassBusy = false;
let overpassPausedUntil = 0;

export class OverpassUnavailableError extends Error {}

async function overpass<T = unknown>(query: string): Promise<T> {
  if (overpassBusy || Date.now() < overpassPausedUntil) {
    throw new OverpassUnavailableError("Overpass ocupado/pausado");
  }
  overpassBusy = true;
  try {
    let lastErr: unknown;
    for (const url of ENDPOINTS) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), OVERPASS_TIMEOUT_MS);
      try {
        const res = await fetch(url, {
          method: "POST",
          body: "data=" + encodeURIComponent(query),
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`Overpass ${res.status}`);
        return (await res.json()) as T;
      } catch (e) {
        lastErr = e;
      } finally {
        clearTimeout(timer);
      }
    }
    overpassPausedUntil = Date.now() + OVERPASS_PAUSE_AFTER_FAIL_MS;
    throw lastErr ?? new Error("Overpass indisponível");
  } finally {
    overpassBusy = false;
  }
}

export type POI = {
  id: number;
  lat: number;
  lng: number;
  kind: "traffic_signals" | "bus_stop";
  name?: string;
};

type OverpassEl = {
  type: string;
  id: number;
  lat?: number;
  lon?: number;
  tags?: Record<string, string>;
};

/** Fetch traffic signals + bus stops inside a bbox. */
export async function fetchPOIs(bbox: {
  south: number;
  west: number;
  north: number;
  east: number;
}): Promise<POI[]> {
  const b = `${bbox.south},${bbox.west},${bbox.north},${bbox.east}`;
  const q = `[out:json][timeout:20];
(
  node["highway"="traffic_signals"](${b});
  node["highway"="bus_stop"](${b});
  node["public_transport"="platform"]["bus"="yes"](${b});
);
out body 800;`;
  const data = await overpass<{ elements: OverpassEl[] }>(q);
  const out: POI[] = [];
  for (const el of data.elements) {
    if (el.lat == null || el.lon == null) continue;
    const tags = el.tags ?? {};
    let kind: POI["kind"] | null = null;
    if (tags.highway === "traffic_signals") kind = "traffic_signals";
    else if (tags.highway === "bus_stop" || tags.public_transport === "platform")
      kind = "bus_stop";
    if (!kind) continue;
    out.push({ id: el.id, lat: el.lat, lng: el.lon, kind, name: tags.name });
  }
  return out;
}

/** Parse an OSM maxspeed tag ("50", "50 km/h", "30 mph") into km/h number. */
export function parseMaxspeed(raw?: string): number | undefined {
  if (!raw) return undefined;
  const m = raw.match(/(\d+(?:\.\d+)?)\s*(mph)?/i);
  if (!m) return undefined;
  const n = parseFloat(m[1]);
  if (Number.isNaN(n)) return undefined;
  return m[2]?.toLowerCase() === "mph" ? Math.round(n * 1.609) : Math.round(n);
}

/** Query maxspeed (km/h) of the nearest tagged way around lat/lng. */
export async function fetchMaxspeedNear(
  lat: number,
  lng: number,
): Promise<number | undefined> {
  const q = `[out:json][timeout:15];
way(around:25,${lat},${lng})["highway"]["maxspeed"];
out tags 1;`;
  const data = await overpass<{
    elements: Array<{ tags?: Record<string, string> }>;
  }>(q);
  const ms = data.elements[0]?.tags?.maxspeed;
  return parseMaxspeed(ms);
}

/* ---------- cache ---------- */

const speedCache = new Map<string, { value?: number; ts: number }>();
// Limite de via é dado estático (não muda em minutos) — TTL longo maximiza
// reaproveitamento do cache por bucket de ~50m e reduz o volume de
// requisições ao Overpass público numa frota grande. Achado no teste de
// carga de 5000 veículos, 2026-09-02 (ver useSpeedLimitAlerts).
const SPEED_TTL_MS = 60 * 60_000;

/** Bucket lat/lng into ~50m cells to reuse maxspeed lookups. */
function bucket(lat: number, lng: number) {
  const f = (n: number) => Math.round(n * 2000) / 2000;
  return `${f(lat)},${f(lng)}`;
}

export async function getCachedMaxspeed(
  lat: number,
  lng: number,
): Promise<number | undefined> {
  const key = bucket(lat, lng);
  const now = Date.now();
  const hit = speedCache.get(key);
  if (hit && now - hit.ts < SPEED_TTL_MS) return hit.value;
  try {
    const value = await fetchMaxspeedNear(lat, lng);
    speedCache.set(key, { value, ts: now });
    return value;
  } catch {
    return hit?.value;
  }
}
